"use client";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const DashboardContext = createContext();
export const useDashboard = () => useContext(DashboardContext);

/**
 * 🔁 Backend → Frontend normalizer
 */
const normalizeDashboard = (data) => ({
  year: data?.year ?? new Date().getFullYear(),
  month: data?.month ?? new Date().getMonth() + 1,

  weeklyUsers:
    data?.graphs?.weekly_users?.map((item) => ({
      week: item.week,
      total: Number(item.total ?? 0),
    })) ?? [],

  weeklyRevenue:
    data?.graphs?.weekly_revenue?.map((item) => ({
      week: item.week,
      total: Number(item.total ?? 0),
    })) ?? [],

  weeklyOrders:
    data?.graphs?.weekly_orders?.map((item) => ({
      week: item.week,
      total: Number(item.total ?? 0),
    })) ?? [],
});

export const DashboardProvider = ({ children }) => {
  const { token, logout } = useAuth();

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const DASHBOARD_ENDPOINT = `${BASE_URL}/dashboard`;

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  // 🔹 FETCH
  const fetchDashboard = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(DASHBOARD_ENDPOINT, { headers });

      if (!res.ok) {
        if (res.status === 401) logout();
        throw new Error("Dashboard API failed");
      }

      const json = await res.json();

      if (json?.status !== 200) {
        throw new Error("Invalid dashboard response");
      }

      setDashboard(normalizeDashboard(json));
    } catch (err) {
      setDashboard(null);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return (
    <DashboardContext.Provider
      value={{
        dashboard,
        loading,
        error,
        refreshDashboard: fetchDashboard,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};